import { useState, useEffect, useCallback } from 'react';

import { confluenceLocalStorageInstance as localStorage } from '@confluence/storage-manager/entry-points/storageInstance.ts';
import { PERSISTED_KEYS_ON_SERVER } from '@confluence/storage-manager/entry-points/LocalStorageKeys.ts';

import { CONFLUENCE_STORAGE_LIMITS, WARNING_THRESHOLD } from '../storageLimits/storageLimits.ts';

const STORAGE_ENFORCEMENT_BANNER_DISMISS_STATE_KEY =
	PERSISTED_KEYS_ON_SERVER.PERSISTED_STORAGE_ENFORCEMENT_BANNER;

type DismissalStateParam = {
	currentUsage: number;
	skip: boolean;
};

const getIsDismissed = () => {
	return String(localStorage?.getItem(STORAGE_ENFORCEMENT_BANNER_DISMISS_STATE_KEY)) === 'true';
};

export const useStorageEnforcementDismissalState = ({
	currentUsage,
	skip,
}: DismissalStateParam) => {
	const [isDismissed, setIsDismissed] = useState<boolean>(getIsDismissed);

	useEffect(() => {
		/**
		 * Reset the dismiss state if they have dismissed the banner + are outside the thresholds to see the dismissible banner
		 * The UX reasoning is to reshow the banner for:
		 *   1. they have dismissed -> gone below the warning threshold -> go back over
		 *   2. They have dismissed -> go above storage limit -> go back below to warning limit
		 */
		if (
			isDismissed &&
			(currentUsage < CONFLUENCE_STORAGE_LIMITS.FREE * WARNING_THRESHOLD ||
				currentUsage >= CONFLUENCE_STORAGE_LIMITS.FREE)
		) {
			localStorage.setItem(STORAGE_ENFORCEMENT_BANNER_DISMISS_STATE_KEY, 'false');
			setIsDismissed(false);
		}
	}, [currentUsage, isDismissed]);

	const onDismiss = useCallback(() => {
		localStorage.setItem(STORAGE_ENFORCEMENT_BANNER_DISMISS_STATE_KEY, 'true');
		setIsDismissed(true);
	}, []);

	return { isDismissed: skip ? false : isDismissed, onDismiss };
};
